import { router } from '@inertiajs/react'
import { ImageOff, Pencil, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { IconPicker } from '@/components/ui/IconPicker'
import { Label } from '@/components/ui/Label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/shadcn/popover'
import { useRouteParams } from '@/hooks/useRoutes'
import { LucideIcon } from '@/lib/lucideIconRegistry'
import { cn } from '@/lib/utils'
import { COVER_PATTERNS, coverBackground, coverSwatchBackground, type CoverPattern } from '@/lib/coverPatterns'
import routes from '@/routeHelpers'
import type { HelpCover } from '@/pages/HelpCenter/types'

interface CoverImagePickerProps {
  resource: 'help_article' | 'help_category'
  id: string
  currentUrl: string | null
  cover: HelpCover
  brandPrimary: string | null
  brandAccent: string | null
  defaultIcon?: string | null
}

const FALLBACK = '#10b981'

const PATTERN_LABELS: Record<CoverPattern, string> = {
  dots: 'Dots',
  'dots-lg': 'Dots (large)',
  grid: 'Grid',
  diagonal: 'Diagonal',
  stripes: 'Stripes',
  crosshatch: 'Crosshatch',
  plus: 'Plus',
  rings: 'Rings',
  chevron: 'Chevron',
  diamonds: 'Diamonds',
  triangles: 'Triangles',
  checker: 'Checker',
  waves: 'Waves',
  confetti: 'Confetti',
  zigzag: 'Zigzag',
}

function CoverIcon({ name, className }: { name: string; className?: string }) {
  if (!name) return null
  return /^[a-z0-9-]+$/.test(name)
    ? <LucideIcon name={name} className={className} />
    : <span className={cn('leading-none', className)}>{name}</span>
}

export function CoverImagePicker({
  resource, id, currentUrl, cover, brandPrimary, brandAccent, defaultIcon,
}: CoverImagePickerProps) {
  const { withAccount } = useRouteParams()
  const inputRef = useRef<HTMLInputElement>(null)
  // Local state only holds the pending choice before a pattern exists; once the
  // server has a cover, its values win so external updates can't leave the
  // swatch/icon highlight stale.
  const [pendingColor, setPendingColor] = useState('primary')
  const [pendingIcon, setPendingIcon] = useState(defaultIcon || '')
  const activeColor = cover.color ?? pendingColor
  const activeIcon = cover.icon ?? pendingIcon

  const routeset = resource === 'help_article' ? routes.helpArticles : routes.helpCategories

  const resolvedColor = (color?: string) => {
    if (!color || color === 'primary') return brandPrimary || FALLBACK
    if (color === 'accent') return brandAccent || brandPrimary || FALLBACK
    return color
  }

  const postPattern = (pattern: string, color: string, icon: string) => {
    router.post(
      routeset.cover.path(withAccount({ id })),
      { pattern, icon: icon || undefined, color },
      { preserveScroll: true },
    )
  }

  const remove = () => {
    router.post(routeset.cover.path(withAccount({ id })), { remove: true }, { preserveScroll: true })
  }

  const pickPattern = (pattern: string) => {
    if (pattern === cover.pattern) remove()
    else postPattern(pattern, activeColor, activeIcon)
  }

  const pickColor = (color: string) => {
    setPendingColor(color)
    if (cover.pattern) postPattern(cover.pattern, color, activeIcon)
  }

  const changeIcon = (icon: string) => {
    setPendingIcon(icon)
    if (cover.pattern) postPattern(cover.pattern, activeColor, icon)
  }

  const upload = (file: File) => {
    const formData = new FormData()
    formData.append(`${resource}[feature_image]`, file)
    router.patch(routeset.update.path(withAccount({ id })), formData, {
      forceFormData: true,
      preserveScroll: true,
    })
  }

  const swatchClass = 'size-7 cursor-pointer rounded-md ring-offset-2 ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  const isCustom = activeColor !== 'primary' && activeColor !== 'accent'
  const hasCover = Boolean(currentUrl || cover.pattern)

  return (
    <div className="group relative aspect-[16/7] overflow-hidden rounded-lg border border-border bg-muted">
      {currentUrl
        ? <img src={currentUrl} alt="Cover" className="size-full object-cover" />
        : cover.pattern
          ? (
            <div
              className="relative size-full"
              style={{ background: coverBackground(cover.pattern as CoverPattern, resolvedColor(cover.color)) }}
            >
              {cover.icon && (
                <CoverIcon
                  name={cover.icon}
                  className="pointer-events-none absolute -right-2 bottom-0 size-28 text-white/20"
                />
              )}
            </div>
          )
          : (
            <div className="flex size-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border text-muted-foreground">
              <ImageOff className="size-6" />
              <span className="text-xs">No cover</span>
            </div>
          )}

      <div className={cn(
        'absolute bottom-2 right-2 flex items-center gap-1.5 transition',
        hasCover && 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
      )}>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="secondary" size="sm" className="shadow-sm">
              <Pencil className="size-3.5" /> {hasCover ? 'Change cover' : 'Add cover'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-3">
            <Tabs defaultValue={currentUrl ? 'upload' : 'pattern'}>
              <TabsList className="h-8 w-full">
                <TabsTrigger value="pattern" className="flex-1 py-1 text-xs">Pattern</TabsTrigger>
                <TabsTrigger value="upload" className="flex-1 py-1 text-xs">Upload</TabsTrigger>
              </TabsList>

              <TabsContent value="pattern" className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    title="Brand colour"
                    aria-label="Brand colour"
                    aria-pressed={activeColor === 'primary'}
                    onClick={() => pickColor('primary')}
                    style={{ backgroundColor: brandPrimary || FALLBACK }}
                    className={cn(swatchClass, activeColor === 'primary' && 'ring-2 ring-ring')}
                  />
                  <button
                    type="button"
                    title="Accent colour"
                    aria-label="Accent colour"
                    aria-pressed={activeColor === 'accent'}
                    onClick={() => pickColor('accent')}
                    style={{ backgroundColor: brandAccent || brandPrimary || FALLBACK }}
                    className={cn(swatchClass, activeColor === 'accent' && 'ring-2 ring-ring')}
                  />
                  <label
                    className={cn(swatchClass, 'relative overflow-hidden', isCustom && 'ring-2 ring-ring')}
                    style={{ backgroundColor: isCustom ? activeColor : 'transparent', backgroundImage: isCustom ? undefined : 'conic-gradient(from 0deg, #ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)' }}
                    title="Custom colour"
                  >
                    <input
                      type="color"
                      value={isCustom ? activeColor : '#10b981'}
                      onChange={e => pickColor(e.target.value)}
                      className="absolute inset-0 size-full cursor-pointer opacity-0"
                      aria-label="Custom colour"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-6 gap-1.5">
                  <button
                    type="button"
                    title="No cover"
                    aria-label="No cover"
                    aria-pressed={!cover.pattern && !currentUrl}
                    onClick={remove}
                    className={cn(
                      'flex aspect-square w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-border bg-muted/40 text-muted-foreground ring-offset-1 ring-offset-background transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      !cover.pattern && !currentUrl && 'ring-2 ring-ring',
                    )}
                  >
                    <ImageOff className="size-4" />
                  </button>
                  {COVER_PATTERNS.map(p => (
                    <button
                      key={p}
                      type="button"
                      title={PATTERN_LABELS[p]}
                      aria-label={PATTERN_LABELS[p]}
                      aria-pressed={cover.pattern === p}
                      onClick={() => pickPattern(p)}
                      style={{ background: coverSwatchBackground(p) }}
                      className={cn(
                        'aspect-square w-full cursor-pointer rounded-md ring-offset-1 ring-offset-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        cover.pattern === p && 'ring-2 ring-ring',
                      )}
                    />
                  ))}
                </div>

                <div className="space-y-1.5">
                  <IconPicker value={activeIcon} onChange={changeIcon} placeholder="Overlay icon (optional)" />
                  <p className="text-xs text-muted-foreground">Overlaid on the pattern (defaults to the category icon).</p>
                </div>
              </TabsContent>

              <TabsContent value="upload" className="mt-3 space-y-2">
                <Label>Upload an image</Label>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                    <Upload className="size-4" /> {currentUrl ? 'Replace' : 'Upload image'}
                  </Button>
                  {hasCover && (
                    <Button type="button" variant="ghost" size="sm" onClick={remove}>
                      <Trash2 className="size-4" /> Remove
                    </Button>
                  )}
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => { const file = e.target.files?.[0]; if (file) { upload(file); e.target.value = '' } }}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
